import { z } from 'zod'
import { MultiPolygon, Polygon } from '@turf/helpers'
import { User } from 'src/common-services/types/user-types'
import {
  patternsHourResponseSchema,
  patternsWeekdayDatesResponseSchema,
  trendsDatumSchema,
} from 'src/common-services/types/footfall-types'
import {
  commonTradeAreaSectionSchema,
  footfallSectionDetailsSchema,
  tradeAreaItemSchema,
} from 'src/common-services/types/trade-area-types'
import { amMobilityProvidersSchema, amDateShema, amMobilityRankScopeSchema } from 'src/common-services/types/alphamap'

export const CUSTOM_POLYGON_AREA_LIMIT = 3_000_000

// Metric Ids
export const TRAFFIC_TRENDS_DAY_ID = 'traffic_trends_day'
export const DAY_OF_WEEK_HOUR_ID = 'day_of_week_hour'
export const DAY_OF_WEEK_ID = 'day_of_week'
export const CATCHMENT_AREA_ORIGIN_HOME = 'catchment_area_origin_home'
export const CATCHMENT_AREA_ORIGIN_WORK = 'catchment_area_origin_work'
export const CATCHMENT_AREA_ORIGIN_HOME_H3 = 'catchment_area_origin_home_h3'

const customFootfallStatusSchema = z.enum(['pending', 'finished', 'error'])
export type CustomFootfallStatus = z.infer<typeof customFootfallStatusSchema>

const customFootfallDateSchema = z.object({
  year: z.number().gte(2010).lte(2099),
  month: z.number().gte(1).lte(12),
  day: z.number().gte(1).lte(31),
})
export type CustomFootfallDate = z.infer<typeof customFootfallDateSchema>

export const YYYYMMDDDateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date needs to be YYYY-MM-DD')
export type YYYYMMDDDateString = z.infer<typeof YYYYMMDDDateStringSchema>

const customFootfallDatasetSchema = z.enum([
  TRAFFIC_TRENDS_DAY_ID,
  DAY_OF_WEEK_HOUR_ID,
  DAY_OF_WEEK_ID,
  CATCHMENT_AREA_ORIGIN_HOME_H3,
])
export type CustomFootfallDataset = z.infer<typeof customFootfallDatasetSchema>

// Request body validator
export const customFootfallRequestSchema = z.object({
  id: z.string().min(1).max(200),
  name: z.string().min(1).max(200),
  polygon: z.object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(z.array(z.array(z.array(z.number())))),
  }),
  dataset: customFootfallDatasetSchema,
  startDate: YYYYMMDDDateStringSchema,
  endDate: YYYYMMDDDateStringSchema,
})
export type CustomFootfallRequest = z.infer<typeof customFootfallRequestSchema>

const customFootfallMetricValueSchema = z.object({
  name: z.string(),
  unit: z.string(),
  number: z.number(),
  count: z.number(),
  category: z.string(),
})
export type CustomFootfallMetricValue = z.infer<typeof customFootfallMetricValueSchema>

const customFootfallDimensionSchema = z.object({
  dimensionId: z.string().optional(),
  dimensionName: z.string().optional(),
  value: z.string(),
})
export type CustomFootfallDimension = z.infer<typeof customFootfallDimensionSchema>

const customFootfallMetricSchema = z.object({
  metricId: z.string(),
  observationPeriod: z.object({
    start: customFootfallDateSchema,
    end: customFootfallDateSchema,
  }),
  mapFeatureV2: z.object({
    layerId: z.string(),
    featureId: z.string(),
    name: z.string(),
    geo: z.string(),
    addressComponentsList: z.array(z.any()),
  }),
  relatedMapFeature: z
    .object({
      layerId: z.string(),
      featureId: z.string(),
      name: z.string(),
      geo: z.string(),
      addressComponentsList: z.array(z.any()),
    })
    .optional(),
  dimensionsList: z.array(customFootfallDimensionSchema),
  supportingValuesList: z.array(customFootfallMetricValueSchema),

  value: z.object({
    name: z.string(),
    unit: z.string(),
    number: z.number(),
    count: z.number(),
    category: z.string(),
  }),
  flagsList: z.array(z.any()),
})
export type CustomFootfallMetric = z.infer<typeof customFootfallMetricSchema>

export type CensusBlockGroup = {
  bgId: string
  geom: MultiPolygon | Polygon
}

export type CensusBlockGroupWithCounts = {
  bgId: string
  count: number
}

// Report body from unacast
const customFootfallReportResponseSchema = z
  .object({
    status: z.literal(customFootfallStatusSchema.Enum.pending).or(z.literal(customFootfallStatusSchema.Enum.error)),
    results: z.undefined(),
  })
  .or(
    z.object({
      status: z.literal(customFootfallStatusSchema.Enum.finished),
      results: z.array(customFootfallMetricSchema),
    })
  )
export type CustomFootfallReportResponse = z.infer<typeof customFootfallReportResponseSchema>

// Cleaned up body i.e. removed all nested jibber jabber
export const customFootfallDataSchema = z.object({
  [TRAFFIC_TRENDS_DAY_ID]: z.array(trendsDatumSchema),
  [DAY_OF_WEEK_HOUR_ID]: patternsHourResponseSchema,
  [DAY_OF_WEEK_ID]: patternsWeekdayDatesResponseSchema,
  [CATCHMENT_AREA_ORIGIN_HOME_H3]: z.array(tradeAreaItemSchema),
})
export type CustomFootfallData = z.infer<typeof customFootfallDataSchema>

// Trade Area
export const customTradeAreaResponseSchema = z.object({
  tradeAreaMap: z.array(footfallSectionDetailsSchema),
  tradeAreaMapOverview: z.array(footfallSectionDetailsSchema),
  tradeAreaDetails: z.object({
    sections: z.array(commonTradeAreaSectionSchema),
  }),
})
export type CustomTradeAreaResponse = z.infer<typeof customTradeAreaResponseSchema>

// Different datasets and their data
const footTrafficTrendsSchema = z.object({
  dataset: z.literal(customFootfallDatasetSchema.Enum.traffic_trends_day),
  data: z.array(trendsDatumSchema).optional(),
})
export type FootTrafficTrends = z.infer<typeof footTrafficTrendsSchema>

const dayOfWeekHourTrendsSchema = z.object({
  dataset: z.literal(customFootfallDatasetSchema.Enum.day_of_week_hour),
  data: patternsHourResponseSchema.optional(),
})
export type DayOfWeekHourTrends = z.infer<typeof dayOfWeekHourTrendsSchema>

const dayOfWeekTrendsSchema = z.object({
  dataset: z.literal(customFootfallDatasetSchema.Enum.day_of_week),
  data: patternsWeekdayDatesResponseSchema.optional(),
})
export type DayOfWeekTrends = z.infer<typeof dayOfWeekTrendsSchema>

const catchmentAreaOriginHomeH3Schema = z.object({
  dataset: z.literal(customFootfallDatasetSchema.Enum.catchment_area_origin_home_h3),
  data: z.array(tradeAreaItemSchema).optional(),
})
export type CatchmentAreaOriginHomeH3 = z.infer<typeof catchmentAreaOriginHomeH3Schema>

const customFootfallDataSetDataSchema = z.union([
  footTrafficTrendsSchema,
  dayOfWeekHourTrendsSchema,
  dayOfWeekTrendsSchema,
  catchmentAreaOriginHomeH3Schema,
])
export type CustomFootfallDatasetData = z.infer<typeof customFootfallDataSetDataSchema>

// Row in custom_footfall table with a specific dataset
export type CustomFootfall<TDataset extends CustomFootfallDatasetData> = {
  userId: User['id']
  id: string
  reportId: string
  name: string
  polygon: MultiPolygon
  startDate: YYYYMMDDDateString
  endDate: YYYYMMDDDateString
  status: CustomFootfallStatus
  createdAt: string
  finishedAt?: string
} & TDataset

export const amCustomPolygonRankPositionRequestSchema = z.object({
  locationId: z.string(),
  placeId: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  date: amDateShema.optional(),
  scope: amMobilityRankScopeSchema.optional(),
  naics: z.array(z.string()),
})

export type AMCustomPolygonRankPositionRequest = z.infer<typeof amCustomPolygonRankPositionRequestSchema>

export const amCustomPolygonRankChartRequestSchema = amCustomPolygonRankPositionRequestSchema.extend({
  buckets: z.number().min(2).max(1000).optional(),
})

export type AMCustomPolygonRankChartRequest = z.infer<typeof amCustomPolygonRankChartRequestSchema>

export const amCustomPolygonRankListRequestSchema = amCustomPolygonRankPositionRequestSchema.extend({
  limit: z.number().min(1),
  offset: z.number().min(0),
})

export type AMCustomPolygonRankListRequest = z.infer<typeof amCustomPolygonRankListRequestSchema>

export const amCustomPolygonRankPositionOverTimeRequestSchema = z.object({
  locationId: z.string(),
  placeId: z.string(),
  provider: amMobilityProvidersSchema.optional(),
  from: amDateShema.optional(),
  to: amDateShema.optional(),
  scope: amMobilityRankScopeSchema.optional(),
  naics: z.array(z.string()),
})

export type AMCustomPolygonRankPositionOverTimeRequest = z.infer<
  typeof amCustomPolygonRankPositionOverTimeRequestSchema
>
